import { useEffect, useRef, useState } from "react";

/**
 * Reveal-on-scroll hook with prefers-reduced-motion support.
 * Respects user's motion preferences (a11y).
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(
  options: IntersectionObserverInit = { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
) {
  const ref = useRef<T | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setRevealed(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setRevealed(true);
          obs.unobserve(e.target);
        }
      });
    }, options);

    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return { ref, revealed };
}
